import {isEqual} from 'lodash';
import React, {FC, ReactNode, useEffect, useMemo, useState} from 'react';
import styled from 'styled-components';

import {KeyboardActionsEnum} from '../../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {Plural, Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {usePrevious} from '../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {AsyncStatusesEnum} from '../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {isDefined} from '../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {collectEnumKeys} from '../../../../../libs/shared/core/src/models/api/types/enumTypes';
import {CellsContext} from '../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {ViewerPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {TextKeyboardShortcutsFormatter} from '../../../../../libs/shared/ui/src/components/keyboard/textKeyboardShortcutFormatter';
import {Loader} from '../../../../../libs/shared/ui/src/components/loaders/loader';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {center, ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {ItemsFormatter} from '../../../../../srcCommon/formatters/itemsFormatter';
import {UseApiState, useFetchApi} from '../../../../../srcCommon/helpers/hookHelpers';
import conversationsSelect2 from '../../../../assets/images/conversationsSelect2.png';
import conversationsSelect3 from '../../../../assets/images/conversationsSelect3.png';
import conversationsSelect4 from '../../../../assets/images/conversationsSelect4.png';
import conversationsSelect5 from '../../../../assets/images/conversationsSelect5.png';
import {useAppDispatch} from '../../../../helpers/appReduxHelpers';
import {buildConversationCountUrl} from '../../../../helpers/models/conversationCountHelpers';
import {ApiConversationCount, ConversationCountModel} from '../../../../models/api/conversationCountModel';
import {ConversationBaseModel} from '../../../../models/api/conversationModel';
import {InboxesFiltersEnum} from '../../../../models/url/models/inboxesUrlModel';
import {InboxesVisualConversationSelection} from '../../../../store/panels/cells/helpers/cellsPanelSelectionHelpers';
import {
  hasSearchInInboxesParameters,
  InboxesParameters,
  isInboxesConversationsParameters,
} from '../../../../store/panels/inboxes/parameters/inboxesParameters';
import {
  computeIsAllExceptSelection,
  computeIsNormalSelection,
} from '../../../../store/panels/inboxes/parameters/inboxesParametersHelpers';
import {navigateViewerAsyncAction} from '../../../../store/panels/viewer/viewerPanelActions';
import {BulkActionsDisabledViewerContent} from '../bulkActions/bulkActionsDisabledViewerContent';
import {ConversationsViewerButtons} from './multiConversationsViewerButtons';

/*
 * Props.
 */

interface MultiConversationsViewerContentProps {
  panelId: ViewerPanelId;
  cellsContext: CellsContext;
  inboxesParameters: InboxesParameters;
  conversationSelection: InboxesVisualConversationSelection;
  conversations: ReadonlyArray<ConversationBaseModel>;
  selectionName: ReactNode;
  onSelectAllClick: () => void;
  onClearSelectionClick: () => void;
  areAllSpamOrTrashed: boolean;
  companyUrl: string;
  isBulkActionRequired: boolean;
  isBulkActionInProgress: boolean;
  shouldHideActionButtons: boolean;
}

/*
 * Style.
 */

const StyledViewerDiv = styled.div`
  grid-area: timeline-and-comment;
  ${center()};
  padding: 16px;
`;

const StyledSummaryDiv = styled.div`
  margin-top: 30px;
  margin-bottom: 4px;
  font-weight: ${fontWeights.medium};
  font-size: ${fontSizes.large};
  line-height: 28px;
  color: ${(p) => p.theme.greys.shade80};

  max-width: 100%;
  ${ellipsis()};
`;

const StyledButton = styled(Button)`
  max-width: 100%;
`;

const StyledButtonContentDiv = styled.div`
  ${ellipsis()}
`;

const StyledViewerImage = styled.img`
  width: 92px;
  height: 67px;
`;

/*
 * Component.
 */

export const MultiConversationsViewerContent: FC<MultiConversationsViewerContentProps> = (props) => {
  const {panelId, inboxesParameters, conversationSelection, companyUrl} = props;
  const previousConversationSelection = usePrevious(conversationSelection);
  const {isFetchingCount, conversationCount} = useConversationCount(
    inboxesParameters,
    conversationSelection,
    companyUrl,
  );
  const dispatch = useAppDispatch();

  useEffect(() => {
    // If the conversation selection didn't change, nothing to do.
    if (isEqual(previousConversationSelection, conversationSelection)) {
      return;
    }

    // If the conversation selection transitioned from `isFullyKnown: false -> true`
    // and we have only one conversation selected, we need to re-trigger a fetch
    if (
      conversationSelection.isFullyKnown &&
      previousConversationSelection &&
      !previousConversationSelection.isFullyKnown &&
      conversationSelection.knownIds.length === 1
    ) {
      dispatch(navigateViewerAsyncAction(panelId, inboxesParameters));
    }
  }, [conversationSelection, dispatch, inboxesParameters, panelId, previousConversationSelection]);

  if (isFetchingCount) {
    return (
      <StyledViewerDiv>
        <Loader />
      </StyledViewerDiv>
    );
  }

  if (props.isBulkActionRequired && props.isBulkActionInProgress) {
    return <BulkActionsDisabledViewerContent />;
  }

  return (
    <StyledViewerDiv>
      <StyledViewerImage alt="" src={findImageSource(props)} />
      {maybeRenderSummary(props, conversationCount)}
      {maybeRenderButton(props)}
      {maybeRenderActions(props)}
    </StyledViewerDiv>
  );
};

/*
 * Summary.
 */

function maybeRenderSummary(
  props: MultiConversationsViewerContentProps,
  conversationCount: number | undefined,
) {
  const renderedSummaryText = maybeRenderSummaryText(props, conversationCount);
  if (!renderedSummaryText) {
    return null;
  }

  return (
    <StyledSummaryDiv>
      <TooltipCoordinator render={() => renderedSummaryText} maxWidth={240}>
        <TooltipOverflowCondition>{renderedSummaryText}</TooltipOverflowCondition>
      </TooltipCoordinator>
    </StyledSummaryDiv>
  );
}

function maybeRenderSummaryText(
  props: MultiConversationsViewerContentProps,
  conversationCount: number | undefined,
) {
  if (computeIsAllExceptSelection(props.inboxesParameters.selection)) {
    const isSelectingAll = props.inboxesParameters.selection.deselectedIds.length === 0;
    return renderAllSelectionSummaryText(props, conversationCount, isSelectingAll);
  }

  const count = props.inboxesParameters.selection.selectedIds.length;
  return (
    <StyledSummaryDiv>
      <Plural
        id="conversationsviewer-someselected"
        value={count}
        one={
          <>
            <strong>1</strong> conversation selected
          </>
        }
        other={
          <>
            <strong>{count}</strong> conversations selected
          </>
        }
      />
    </StyledSummaryDiv>
  );
}

function renderAllSelectionSummaryText(
  props: MultiConversationsViewerContentProps,
  conversationCount: number | undefined,
  // We might be in an "AllExceptSelection" with some ID's deselected, in which
  // case we don't want to render "All" at the beginning of the text
  isSelectingAll: boolean,
) {
  const {selectionName} = props;
  const renderedFilters = renderFilters(props);

  const allTransWithSpace = (
    <>
      <Trans id="conversationsviewer-all-selected">All</Trans>&nbsp;
    </>
  );

  if (renderedFilters && isDefined(conversationCount)) {
    return (
      <>
        {isSelectingAll && allTransWithSpace}
        <Trans id="conversationsviewer-allselectedexcept-countfilters">
          <strong>{conversationCount}</strong> {renderedFilters} conversations in {selectionName} selected
        </Trans>
      </>
    );
  }

  if (renderedFilters) {
    return (
      <Trans id="conversationsviewer-allselectedexcept-filters">
        <strong>All</strong> {renderedFilters} conversations in {selectionName} selected
      </Trans>
    );
  }

  if (isDefined(conversationCount)) {
    return (
      <>
        {isSelectingAll && allTransWithSpace}
        <Trans id="conversationsviewer-allselectedexcept-count">
          <strong>{conversationCount}</strong> conversations in {selectionName} selected
        </Trans>
      </>
    );
  }

  return (
    <Trans id="conversationsviewer-allselectedexcept">
      <strong>All</strong> conversations in {selectionName} selected
    </Trans>
  );
}

/*
 * Button.
 */

function maybeRenderButton(props: MultiConversationsViewerContentProps) {
  if (props.shouldHideActionButtons) {
    return null;
  }

  const renderedText = maybeRenderButtonText(props);
  if (!renderedText) {
    return null;
  }

  const onClick = computeIsAllExceptSelection(props.inboxesParameters.selection)
    ? props.onClearSelectionClick
    : props.onSelectAllClick;

  return (
    <StyledButton onClick={onClick} variant={ButtonVariantsEnum.LINK}>
      <StyledButtonContentDiv>
        <TooltipCoordinator render={() => renderedText} maxWidth={240}>
          <TooltipOverflowCondition>{renderedText}</TooltipOverflowCondition>
        </TooltipCoordinator>
      </StyledButtonContentDiv>
    </StyledButton>
  );
}

function maybeRenderButtonText(props: MultiConversationsViewerContentProps) {
  if (computeIsAllExceptSelection(props.inboxesParameters.selection)) {
    return <Trans id="conversationsviewer-clearselection-cta">Clear selection</Trans>;
  }

  return renderSelectAllButtonText(props);
}

function renderSelectAllButtonText(props: MultiConversationsViewerContentProps) {
  const renderedFilters = renderFilters(props);
  const renderedShortcut = <TextKeyboardShortcutsFormatter action={KeyboardActionsEnum.SELECT_ALL} />;

  if (hasSearchInInboxesParameters(props.inboxesParameters) && renderedFilters) {
    return (
      <Trans id="conversationsviewer-selectallsearch-ctafilters">
        Select all {renderedFilters} conversations that match {props.selectionName} ({renderedShortcut})
      </Trans>
    );
  }

  if (hasSearchInInboxesParameters(props.inboxesParameters)) {
    return (
      <Trans id="conversationsviewer-selectallsearch-cta">
        Select all conversations that match {props.selectionName} ({renderedShortcut})
      </Trans>
    );
  }

  if (renderedFilters) {
    return (
      <Trans id="conversationsviewer-selectall-ctafilters">
        Select all {renderedFilters} conversations in {props.selectionName} ({renderedShortcut})
      </Trans>
    );
  }

  return (
    <Trans id="conversationsviewer-selectall-cta">
      Select all conversations in {props.selectionName} ({renderedShortcut})
    </Trans>
  );
}

/*
 * Filters.
 */

function renderFilters(props: MultiConversationsViewerContentProps) {
  const {inboxesParameters} = props;
  if (!isInboxesConversationsParameters(inboxesParameters)) {
    // Drafts and sent have no filters.
    return null;
  }

  if (hasSearchInInboxesParameters(inboxesParameters)) {
    // Search has no filters.
    return null;
  }

  const {filters} = inboxesParameters;
  if (filters.length === 0) {
    return null;
  }

  return (
    <ItemsFormatter
      items={filters.map((filter) => ({id: filter, name: filterEnumToNameMap[filter]}))}
      limit={collectEnumKeys(InboxesFiltersEnum).length}
    />
  );
}

const filterEnumToNameMap: Record<InboxesFiltersEnum, ReactNode> = {
  [InboxesFiltersEnum.UNASSIGNED]: <Trans id="conversationselection-filter-unassigned">unassigned</Trans>,
  [InboxesFiltersEnum.OPEN]: <Trans id="conversationselection-filter-open">open</Trans>,
  [InboxesFiltersEnum.ASSIGNED]: <Trans id="conversationselection-filter-assigned">assigned</Trans>,
  [InboxesFiltersEnum.ARCHIVED]: <Trans id="conversationselection-filter-archived">archived</Trans>,
  [InboxesFiltersEnum.SNOOZED]: <Trans id="conversationselection-filter-snoozed">snoozed</Trans>,
  [InboxesFiltersEnum.TRASHED]: <Trans id="conversationselection-filter-trashed">trashed</Trans>,
  [InboxesFiltersEnum.SPAM]: <Trans id="conversationselection-filter-soam">spam</Trans>,
  [InboxesFiltersEnum.ALL]: undefined,
};

/*
 * Conversation Count.
 */

function useConversationCount(
  inboxesParameters: InboxesParameters,
  conversationSelection: InboxesVisualConversationSelection,
  companyUrl: string,
) {
  const path = useMemo(
    () => buildConversationCountUrl(companyUrl, inboxesParameters),
    [inboxesParameters, companyUrl],
  );
  const pathToFetch =
    computeIsAllExceptSelection(inboxesParameters.selection) && !conversationSelection.isFullyKnown
      ? path
      : undefined;

  const [conversationCountState, setConversationCountState] = useState<UseApiState<ConversationCountModel>>();
  const fetchConversationCount = useFetchApi({
    apiType: ApiConversationCount,
    onStateChange: setConversationCountState,
  });

  const previousPathToFetch = usePrevious(pathToFetch);
  useEffect(() => {
    if (!pathToFetch || pathToFetch === previousPathToFetch) {
      return;
    }

    fetchConversationCount(pathToFetch);
  });

  // TODO: Cancel the request if path is undefined.

  if (computeIsNormalSelection(inboxesParameters.selection)) {
    // We don't need to fetch the count for normal selections.
    return {
      isFetchingCount: false,
      conversationCount: inboxesParameters.selection.selectedIds.length,
    };
  }

  if (conversationSelection.isFullyKnown) {
    // We don't need to fetch the count when the selection is fully known.
    return {
      isFetchingCount: false,
      conversationCount: conversationSelection.knownIds.length,
    };
  }

  if (!path) {
    // We can't fetch the count for the given parameters.
    return {
      isFetchingCount: false,
      conversationCount: undefined,
    };
  }

  if (conversationCountState?.asyncStatus === AsyncStatusesEnum.LOADED && conversationCountState.result) {
    // We've fetched the count for the given parameters.
    return {
      isFetchingCount: false,
      conversationCount:
        conversationCountState.result.total - inboxesParameters.selection.deselectedIds.length,
    };
  }

  // We're fetching the count
  return {
    isFetchingCount: conversationCountState?.asyncStatus === AsyncStatusesEnum.LOADING,
    conversationCount: undefined,
  };
}

/*
 * Helpers.
 */

function maybeRenderActions(props: MultiConversationsViewerContentProps) {
  if (props.conversations.length === 0 || props.shouldHideActionButtons) {
    return null;
  }

  return (
    <ConversationsViewerButtons
      cellsContext={props.cellsContext}
      inboxesParameters={props.inboxesParameters}
      conversations={props.conversations}
      isBulkActionRequired={props.isBulkActionRequired}
      panelId={props.panelId}
      areAllSpamOrTrashed={props.areAllSpamOrTrashed}
    />
  );
}

function findImageSource(props: MultiConversationsViewerContentProps) {
  const {conversationSelection} = props;

  if (!conversationSelection.isFullyKnown) {
    return conversationsSelect5;
  }

  switch (conversationSelection.knownIds.length) {
    case 2:
      return conversationsSelect2;
    case 3:
      return conversationsSelect3;
    case 4:
      return conversationsSelect4;
    default:
      return conversationsSelect5;
  }
}
